/**
 * Parse countries from aitools/onboarding formats into a string[].
 * Mirrors student-app parseCountries (kandor-frontend).
 */
export function parseCountries(value) {
	if (Array.isArray(value)) {
		return value.map(item => String(item).trim()).filter(Boolean)
	}
	if (value == null) return []
	if (typeof value === 'string') {
		const trimmed = value.trim()
		if (!trimmed) return []
		if (trimmed.startsWith('[')) {
			try {
				const parsed = JSON.parse(trimmed)
				return Array.isArray(parsed)
					? parsed.map(item => String(item).trim()).filter(Boolean)
					: [trimmed]
			} catch {
				return trimmed
					.split(',')
					.map(item => item.trim())
					.filter(Boolean)
			}
		}
		if (trimmed.includes(',')) {
			return trimmed
				.split(',')
				.map(item => item.trim())
				.filter(Boolean)
		}
		return [trimmed]
	}
	return []
}

function isTruthyField(value) {
	if (value == null) return false
	if (typeof value === 'string') return value.trim().length > 0
	if (Array.isArray(value)) return value.length > 0
	return true
}

function pickField(layers, fieldName) {
	for (const { layer, source } of layers) {
		if (!source) continue
		const value = source[fieldName]
		if (isTruthyField(value)) {
			return { value, layer }
		}
	}
	return { value: fieldName === 'countries' ? [] : '', layer: null }
}

function normalizeResearchSource(source) {
	if (!source || typeof source !== 'object') return null
	return {
		category: source.category ?? '',
		subCategory: source.subCategory ?? '',
		selectedPlan: source.selectedPlan ?? '',
		career: source.career ?? '',
		course: source.course ?? '',
		countries: parseCountries(source.countries),
		highestLevel: source.highestLevel ?? '',
	}
}

function onboardingToResearchSource(onboarding) {
	if (!onboarding || typeof onboarding !== 'object') return null
	return {
		category: onboarding.category ?? '',
		subCategory: onboarding.subCategory ?? '',
		selectedPlan: '',
		career: '',
		course: '',
		countries: parseCountries(onboarding.dreamCountry ?? onboarding.DreamCountry),
		highestLevel: '',
	}
}

const RESEARCH_FIELDS = [
	'category',
	'subCategory',
	'selectedPlan',
	'career',
	'course',
	'countries',
	'highestLevel',
]

/**
 * Resolve Research defaults from raw sources. Frontend-only merge.
 *
 * Journey never participates.
 * No student: Counselor Personal Profile → Empty
 * Student selected: Counselor Student Profile → Student AI Profile → Onboarding → Empty
 *
 * @returns {{ defaults: object, sourceByField: object }}
 */
export function resolveResearchDefaults({
	selectedStudentId = null,
	personalSource = null,
	studentProfileSource = null,
	studentJourneySource = null, // deprecated alias for studentProfileSource
	studentSource = null,
	onboardingSource = null,
} = {}) {
	const hasStudent = Boolean(String(selectedStudentId || '').trim())
	const counselorStudentProfile = studentProfileSource ?? studentJourneySource

	const layers = hasStudent
		? [
				{
					layer: 'counselorStudentProfile',
					source: normalizeResearchSource(counselorStudentProfile),
				},
				{ layer: 'student', source: normalizeResearchSource(studentSource) },
				{ layer: 'onboarding', source: onboardingToResearchSource(onboardingSource) },
			]
		: [{ layer: 'personal', source: normalizeResearchSource(personalSource) }]

	const defaults = {}
	const sourceByField = {}

	for (const field of RESEARCH_FIELDS) {
		const { value, layer } = pickField(layers, field)
		defaults[field] = field === 'countries' ? value : value || ''
		sourceByField[field] = layer
	}

	return { defaults, sourceByField }
}
